import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { PromptRulesPanel } from '../components/PromptRulesPanel';
import { ReferrerTypeManagerPanel } from '../components/ReferrerTypeManagerPanel';
import { ClinicLocationsManagerPanel } from '../components/ClinicLocationsManagerPanel';
import { LocationReferenceManagerPanel } from '../components/LocationReferenceManagerPanel';
import { PromptPreviewModal } from '../components/PromptPreviewModal';
import { CreateRuleModal } from '../components/CreateRuleModal';
import { SkillBodyMap } from '../components/SkillBodyMap';
import { SkillScenarioPanel } from '../components/SkillScenarioPanel';
import { PromptRuleService, type GeneratedPrompt, type PromptRule } from '../services/promptRuleService';
import { Spinner } from '../components/Spinner';
import '../styles/skill-manager.css';

type RuleSkillType = 'referral' | 'triage' | 'location' | 'clinic_selection' | 'referrer_type';
type SkillType = 'body_map' | 'scenario' | RuleSkillType;

const SKILL_LABELS: Record<SkillType, string> = {
  body_map: 'Body Map',
  scenario: 'Scenario Check',
  referral: 'Referral Extraction',
  triage: 'Triage',
  location: 'Location Extraction',
  clinic_selection: 'Clinic Selection',
  referrer_type: 'Referrer Type',
};

const SKILL_DESCRIPTIONS: Record<SkillType, string> = {
  body_map: 'See which skills apply to each part of the body. Skills that run on every referral are listed separately as everyday skills.',
  scenario: 'Describe a referral you have in mind and check which urgency skills it would meet and the priority they lead to.',
  referral: 'Rules that control how patient and referral fields are extracted from incoming PDF referrals.',
  triage: 'Rules that guide the AI when categorising referrals by complexity and urgency.',
  location: 'Rules for extracting patient residential suburb/city and referring practice suburb/city from referral text.',
  clinic_selection: 'Rules describing the clinic routing strategy — distance thresholds, confidence weights, and fallback behaviour.',
  referrer_type: 'Rules for AI fallback classification of referring organisation type when deterministic matching does not resolve.',
};

export const SkillManagerPage: React.FC = () => {
  const [skillType, setSkillType] = useState<SkillType>('body_map');
  const [rules, setRules] = useState<PromptRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState<GeneratedPrompt | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sectionFilter, setSectionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const isBodyMap = skillType === 'body_map' || skillType === 'scenario';

  useEffect(() => {
    if (skillType !== 'body_map' && skillType !== 'scenario') {
      loadRules(skillType);
    }
  }, [skillType]);

  const loadRules = async (type: RuleSkillType) => {
    try {
      setIsLoading(true);
      setError(null);
      const loaded = await PromptRuleService.getRules(type);
      setRules(loaded || []);
    } catch (err: any) {
      console.error('Failed to load skill rules:', err);
      setError('Failed to load skill rules. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneratePrompt = async () => {
    try {
      setIsLoading(true);
      const prompt = await PromptRuleService.generatePrompt(skillType as any);
      setGeneratedPrompt(prompt);
      setShowPreview(true);
    } catch (err: any) {
      console.error('Failed to generate skill prompt:', err);
      setError('Failed to generate prompt. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRuleUpdated = () => {
    if (skillType !== 'body_map' && skillType !== 'scenario') loadRules(skillType);
  };

  const handleRuleCreated = (newRule: PromptRule) => {
    setRules(prev => [...prev, newRule]);
    setShowCreateModal(false);
  };

  const filteredRules = rules.filter(rule => {
    const matchesSection = sectionFilter === 'all' || rule.section === sectionFilter;
    const matchesStatus = statusFilter === 'all' || rule.status === statusFilter;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      searchTerm === '' ||
      (rule.section?.toLowerCase() ?? '').includes(searchLower) ||
      (rule.field_name?.toLowerCase() ?? '').includes(searchLower) ||
      (rule.description?.toLowerCase() ?? '').includes(searchLower) ||
      (rule.rule_text?.toLowerCase() ?? '').includes(searchLower);
    return matchesSection && matchesStatus && matchesSearch;
  });

  const sections = Array.from(new Set(rules.map(r => r.section))).sort();
  const activeRuleCount = rules.filter(r => r.status === 'active').length;
  const testingRuleCount = rules.filter(r => r.status === 'testing').length;

  const canPreview = skillType === 'referral' || skillType === 'triage';

  return (
    <Layout>
      <div className="skill-manager-page">
        <div className="skill-manager-header">
          <h1>Skill Manager</h1>
          <p className="page-description">
            {SKILL_DESCRIPTIONS[skillType]}
          </p>

          <div className="skill-type-toggle">
            {(Object.keys(SKILL_LABELS) as SkillType[]).map(type => (
              <button
                key={type}
                className={`toggle-button ${skillType === type ? 'active' : ''}`}
                onClick={() => {
                  setSkillType(type);
                  setSectionFilter('all');
                }}
              >
                {SKILL_LABELS[type]}
              </button>
            ))}
          </div>
        </div>

        {skillType === 'body_map' ? (
          <SkillBodyMap />
        ) : skillType === 'scenario' ? (
          <SkillScenarioPanel />
        ) : (
          <>
            <div className="skill-stats-bar">
              <div className="stat">
                <span className="stat-label">Total Rules</span>
                <span className="stat-value">{rules.length}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Active</span>
                <span className="stat-value stat-active">{activeRuleCount}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Testing</span>
                <span className="stat-value stat-testing">{testingRuleCount}</span>
              </div>
              {canPreview && (
                <button
                  className="btn btn-primary btn-generate-prompt"
                  onClick={handleGeneratePrompt}
                  disabled={isLoading}
                >
                  {isLoading ? 'Generating...' : `Generate ${SKILL_LABELS[skillType]} Prompt`}
                </button>
              )}
              <button
                className="btn btn-success btn-create-rule"
                onClick={() => setShowCreateModal(true)}
                disabled={isLoading}
              >
                + Create Rule
              </button>
            </div>

            {error && (
              <div className="error-banner">
                <span>{error}</span>
                <button className="btn-close" onClick={() => setError(null)}>✕</button>
              </div>
            )}

            <div className="skill-filters">
              <div className="filter-group">
                <label htmlFor="section-filter">Section:</label>
                <select
                  id="section-filter"
                  value={sectionFilter}
                  onChange={e => setSectionFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Sections</option>
                  {sections.map(section => (
                    <option key={section} value={section}>
                      {section.replace(/_/g, ' ').toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="status-filter">Status:</label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="testing">Testing</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="filter-group search">
                <input
                  type="text"
                  placeholder="Search rules..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            {isLoading && !rules.length ? (
              <div className="loading-state">
                <Spinner size="md" />
                <p>Loading skill rules...</p>
              </div>
            ) : filteredRules.length > 0 ? (
              <>
                <PromptRulesPanel rules={filteredRules} onRuleUpdated={handleRuleUpdated} />
                {skillType === 'location' && <LocationReferenceManagerPanel />}
                {skillType === 'clinic_selection' && <ClinicLocationsManagerPanel />}
                {skillType === 'referrer_type' && <ReferrerTypeManagerPanel />}
              </>
            ) : (
              <div className="empty-state">
                <p>No skill rules found for {SKILL_LABELS[skillType]}.</p>
                {skillType === 'location' && <LocationReferenceManagerPanel />}
                {skillType === 'clinic_selection' && <ClinicLocationsManagerPanel />}
                {skillType === 'referrer_type' && <ReferrerTypeManagerPanel />}
                {searchTerm && (
                  <button className="btn btn-link" onClick={() => setSearchTerm('')}>
                    Clear search
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {showPreview && generatedPrompt && (
          <PromptPreviewModal
            prompt={generatedPrompt}
            promptType={skillType as any}
            onClose={() => {
              setShowPreview(false);
              setGeneratedPrompt(null);
            }}
          />
        )}

        {showCreateModal && !isBodyMap && (
          <CreateRuleModal
            onClose={() => setShowCreateModal(false)}
            onRuleCreated={handleRuleCreated}
            existingSections={sections}
            promptType={skillType as any}
          />
        )}
      </div>
    </Layout>
  );
};
